import type { TaskViewModel } from '@trinnvis/dabih-api-client';
import type { PdfField } from 'src/pdf/types';
import type { EmergencyContact } from 'src/outskirts/account/emergency/emergency-view-model';
import type { DocForLinking } from 'src/store/selectors/linking/docForLinkingType';
import { toDocsForLinkingIndex } from 'src/store/selectors/linking/toDocsForLinkingIndex';
import { convertInternalLinks } from 'src/models/internal-linking';
import { t } from 'src/i18n';

function prepareProcedureForMeasuresCardPdf(htmlString: string, partnersAndContacts: EmergencyContact[]): string {
  const doc = new DOMParser().parseFromString(`<div id="root">${htmlString}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) {
    return htmlString;
  }
  root.querySelectorAll('li p').forEach((p) => {
    p.replaceWith(...p.childNodes);
  });
  root.querySelectorAll('a').forEach((a) => {
    const doctype = a.getAttribute('data-internal-link-doctype');
    if (doctype === 'partners' || doctype === 'contacts' || doctype === 'employees') {
      const id = a.getAttribute('data-internal-link-id');
      if (id) {
        const partnerOrContact = partnersAndContacts.find((p) => p.uuid === id);
        if (partnerOrContact && (partnerOrContact.phone || partnerOrContact.email)) {
          const parts: string[] = [];
          if (partnerOrContact.phone) {
            parts.push(t('riskAssessments.measuresCardPdfPhone', { phone: partnerOrContact.phone }));
          }
          if (partnerOrContact.email) {
            parts.push(t('riskAssessments.measuresCardPdfEmail', { email: partnerOrContact.email }));
          }
          a.textContent = (a.textContent ?? '') + ' (' + parts.join(', ') + ')';
        }
      }
    }
    a.replaceWith(...a.childNodes);
  });
  return root.innerHTML;
}

export function getPdfFieldsForMeasuresCard(
  tasks: TaskViewModel[],
  functionsInDisplayOrder: { uuid: string; name: string }[],
  partnersAndContacts: EmergencyContact[],
  docsForLinking: DocForLinking[],
): PdfField[] {
  const functionIndexByUuid = new Map(functionsInDisplayOrder.map((f, index) => [f.uuid, index]));
  // Rutinene ligger lagret med lenketeksten som gjaldt da lenken ble satt inn.
  // Samme konvertering som tiltakskortet på skjerm gjør, slik at pdf-en viser
  // dokumentets navn slik det er nå og ikke et utdatert navn.
  const docsIndex = toDocsForLinkingIndex(docsForLinking);

  const tasksByFunctionUuid = new Map<string, { name: string; procedure: string }[]>();
  tasks.forEach((task) => {
    const tasksForFunction = tasksByFunctionUuid.get(task.functionUuid) ?? [];
    tasksForFunction.push({
      name: task.name ?? '',
      procedure: prepareProcedureForMeasuresCardPdf(
        convertInternalLinks(task.procedures ?? '', docsIndex),
        partnersAndContacts,
      ),
    });
    tasksByFunctionUuid.set(task.functionUuid, tasksForFunction);
  });

  const functionsWithTasks = Array.from(tasksByFunctionUuid.entries())
    .map(([uuid, functionTasks]) => {
      const func = functionsInDisplayOrder.find((f) => f.uuid === uuid);
      const sortedTasks = [...functionTasks].sort((a, b) => a.name.localeCompare(b.name));
      return { uuid, name: func?.name ?? '', tasks: sortedTasks };
    })
    .sort((a, b) => {
      const indexA = functionIndexByUuid.get(a.uuid) ?? -1;
      const indexB = functionIndexByUuid.get(b.uuid) ?? -1;
      if (indexA === -1 && indexB === -1) {
        return a.name.localeCompare(b.name);
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  return functionsWithTasks.map((f) => ({
    type: 'function-with-tasks',
    label: f.name,
    tasks: f.tasks.map((t) => ({ type: 'view-html', label: t.name, text: t.procedure })),
  }));
}
